import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  IconButton,
  Container,
  Grid,
  Divider,
  Avatar,
  AppBar,
  Toolbar,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack,
  ShoppingCart,
  Add,
  Remove,
  AccessTime,
  Star,
  Store
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';
import BottomNavigation from '../components/BottomNavigation';
import { productAPI, providerAPI } from '../services/api';
import useResponsive from '../hooks/useResponsive';
import BrandLoader from '../components/BrandLoader';
import { formatCurrency, getCountryCode } from '../utils/countryUtils';

const ProductDetails = () => {
  const { productId, providerId } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart, getItemQuantity, totalItems, removeFromCart, items } = useCart();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  
  const [product, setProduct] = useState(null);
  const [provider, setProvider] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = Description, 1 = How to Use

  useEffect(() => {
    const fetchProductAndProvider = async () => {
 //     console.log('🔍 ProductDetails: useEffect triggered');
 //     console.log('🔍 ProductDetails: productId from params:', productId);
//      console.log('🔍 ProductDetails: providerId from params:', providerId);
      
      try {
        setLoading(true);
  //      console.log('🔍 ProductDetails: Calling productAPI.getProductDetails with:', productId);
        const response = await productAPI.getProductDetails(productId);
 //       console.log('🔍 ProductDetails: API response received:', response);
        
        // Handle the response format from the backend
        let productData;
        if (response && response.success && response.data) {
          // Backend returns { success: true, data: productData }
          productData = response.data;
        } else if (response && response._id) {
          // Direct product data
          productData = response;
        } else {
          console.error('❌ ProductDetails: Invalid response format:', response);
          throw new Error('Invalid response format from server');
        }
        
  //      console.log('🔍 ProductDetails: Processed product data:', productData);
        
        if (productData) {
          setProduct(productData);
          
          // Use provider data from product if available, otherwise use default
          if (productData.providerId && productData.providerId !== 'store' && productData.providerId !== 'nistylez-platform') {
            try {
              const providerData = await providerAPI.getProviderById(productData.providerId);
              setProvider(providerData);
            } catch (error) {
            //  console.log('🔍 ProductDetails: Provider not found, using default provider');
              setProvider({
                _id: 'nistylez-platform',
                name: 'NISTYLEZ Store',
                type: 'Store',
                image: '/logo.png',
                rating: 5.0,
                reviewCount: 456,
                distance: 1.2
              });
            }
          } else {
            // Use default provider for store products
            setProvider({
              _id: 'nistylez-platform',
              name: 'NISTYLEZ Store',
              type: 'Store',
              image: '/logo.png',
              rating: 5.0,
              reviewCount: 456,
              distance: 1.2
            });
          }
        } else {
          // No product data found - show error
          console.error('❌ ProductDetails: No product data found for ID:', productId);
          throw new Error('Product not found');
        }
      } catch (err) {
        setError(err);
        console.error("Error fetching product or provider:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductAndProvider();
  }, [productId, providerId]);

  const handleAddToCart = () => {
    if (product) {
      const productWithOptions = {
        ...product,
        id: product._id, // Add id field for cart context
        price: product.customerSalePrice || product.customerPrice || product.price, // Use customer pricing
        originalPrice: product.customerPrice, // Use customer original price
        quantity: 1 // Always add 1 quantity when clicking Add to Cart
      };
      addToCart(productWithOptions);
      setShowSuccessMessage(true);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
      // Update cart with new quantity
      const productWithOptions = {
        ...product,
        id: product._id, // Add id field for cart context
        price: product.customerSalePrice || product.customerPrice || product.price, // Use customer pricing
        originalPrice: product.customerPrice, // Use customer original price
        quantity: newQuantity
      };
      addToCart(productWithOptions);
    } else if (newQuantity <= 0) {
      // Remove from cart if quantity goes to 0 or below
      // Find the cart item ID for this product
      const cartItem = items.find(item => item.id === product._id);
      if (cartItem) {
        removeFromCart(cartItem.cartItemId);
      }
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        sx={{
          color: index < Math.floor(rating) ? '#ffd700' : '#e0e0e0',
          fontSize: 14
        }}
      />
    ));
  };

  const calculateDiscountPercentage = (originalPrice, currentPrice) => {
    if (!originalPrice || !currentPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  };

  // Helper function to strip HTML tags from description
  const stripHtmlTags = (htmlString) => {
    if (!htmlString) return '';
    // Create a temporary div element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    // Return only the text content
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <BrandLoader />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Box>
    );
  }

  if (!product || !provider) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Product not found</Typography>
      </Box>
    );
  }

  const cartItemExists = product ? items.some(item => item.id === product._id) : false;
  const cartQuantity = product ? items.find(item => item.id === product._id)?.quantity || 0 : 0;

  return (
    <Box sx={{ 
      flexGrow: 1, 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Toolbar sx={{ minHeight: '56px !important' }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 500 }}>
            Product Details
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        pb: 8 // Space for bottom action bar
      }}>
        {/* Product Image */}
        <Box sx={{ 
          position: 'relative', 
          width: '100%', 
          height: { xs: 280, sm: 320 },
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <CardMedia
            component="img"
            image={product.primaryImage || product.image || (product.images && product.images[0])}
            alt={product.name}
            sx={{ 
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              backgroundColor: '#ffffff'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          {/* Fallback for broken images */}
          <Box
            sx={{
              display: 'none',
              width: '100%',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              color: '#666'
            }}
          >
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              {product.name}
            </Typography>
          </Box>
        </Box>

        {/* Product Content */}
        <Box sx={{ px: 2, py: 3 }}>
          {/* Product Info Card */}
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
            mb: 3,
            border: '1px solid #f0f0f0',
            overflow: 'visible'
          }}>
            <CardContent sx={{ p: 3 }}>
              {/* Product Name */}
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                mb: 2, 
                fontSize: '1.3rem',
                lineHeight: 1.3,
                color: '#1a1a1a'
              }}>
                {product.name}
              </Typography>
              
              {/* Category and Brand */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip 
                  label={product.category || 'General'} 
                  color="primary" 
                  size="small" 
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
                <Chip 
                  label={product.brand || 'NISTYLEZ'} 
                  color="secondary" 
                  size="small" 
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
                {product.inStock ? (
                  <Chip 
                    label="In Stock" 
                    color="success" 
                    size="small" 
                    sx={{ fontSize: '0.75rem', height: 24 }}
                  />
                ) : (
                  <Chip 
                    label="Out of Stock" 
                    color="error" 
                    size="small" 
                    sx={{ fontSize: '0.75rem', height: 24 }}
                  />
                )}
              </Box>
              
              {/* Rating */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                {Array.from({ length: 5 }, (_, index) => (
                  <Star
                    key={index}
                    sx={{
                      color: '#ffd700',
                      fontSize: 14
                    }}
                  />
                ))}
              </Box>

              {/* Pricing Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 800, 
                    fontSize: '1.8rem',
                    color: '#1976d2'
                  }}>
                    {formatCurrency(product.customerSalePrice || product.customerPrice || product.price)}
                  </Typography>
                  {product.customerPrice && product.customerSalePrice && product.customerPrice > product.customerSalePrice && (
                    <Chip
                      label={`${product.customerDiscountPercentage || calculateDiscountPercentage(product.customerPrice, product.customerSalePrice)}% OFF`}
                      sx={{
                        backgroundColor: '#ff6b35',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        height: 28
                      }}
                    />
                  )}
                </Box>
                
                {/* Original Price */}
                {product.customerPrice && product.customerSalePrice && product.customerPrice > product.customerSalePrice && (
                  <Typography variant="h6" sx={{ 
                    textDecoration: 'line-through', 
                    color: '#999', 
                    fontSize: '1.1rem',
                    mb: 1
                  }}>
                    {formatCurrency(product.customerPrice)}
                  </Typography>
                )}
                
                {/* Savings Info */}
                {product.customerSalePrice && product.customerSalePrice < (product.customerPrice || product.price) && (
                  <Typography variant="body1" sx={{ 
                    color: '#2e7d32', 
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}>
                    Save {formatCurrency(((product.customerPrice || product.price) - product.customerSalePrice))}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Tabs for Description and How to Use */}
              <Box sx={{ mb: 2 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{
                  mb: 2, 
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.95rem'
                    },
                    '& .Mui-selected': {
                      color: '#1976d2'
                    }
                  }}
                >
                  <Tab label="Description" />
                  <Tab label="How to Use" disabled={!product.howToUse} />
                </Tabs>

                {/* Description Tab */}
                {activeTab === 0 && (
                  <Box>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        lineHeight: 1.6, 
                        fontSize: '0.9rem',
                        color: '#555',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        '& strong, & b': { fontWeight: 700, color: '#1a1a1a' },
                        '& em, & i': { fontStyle: 'italic' },
                        '& ul': { paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' },
                        '& ol': { paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' },
                        '& li': { marginBottom: '4px' },
                        '& p': { marginBottom: '8px' },
                        '& h1, & h2, & h3, & h4, & h5, & h6': { 
                  fontWeight: 600,
                          marginTop: '12px', 
                          marginBottom: '8px',
                  color: '#1a1a1a'
                        }
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: product.description || product.detailedDescription || 'No description available.' 
                      }}
                    />
                  </Box>
                )}

                {/* How to Use Tab */}
                {activeTab === 1 && (
                  <Box>
                    {product.howToUse ? (
                      <Typography 
                        variant="body1" 
                        sx={{ 
                  lineHeight: 1.6, 
                  fontSize: '0.9rem',
                          color: '#555',
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          '& strong, & b': { fontWeight: 700, color: '#1a1a1a' },
                          '& em, & i': { fontStyle: 'italic' },
                          '& ul': { paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' },
                          '& ol': { paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' },
                          '& li': { marginBottom: '4px' },
                          '& p': { marginBottom: '8px' },
                          '& h1, & h2, & h3, & h4, & h5, & h6': { 
                            fontWeight: 600, 
                            marginTop: '12px', 
                            marginBottom: '8px',
                            color: '#1a1a1a'
                          }
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: product.howToUse 
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No usage instructions available for this product.
                </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Provider Info Card */}
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
            mb: 3,
            border: '1px solid #f0f0f0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar 
                  src={provider.image} 
                  sx={{ width: 50, height: 50 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    fontSize: '1rem',
                    color: '#1a1a1a',
                    mb: 0.5
                  }}>
                    {provider.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {renderStars(provider.rating)}
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                      {provider.rating} ({provider.reviewCount} reviews)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Store sx={{ color: 'primary.main', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                      {provider.type}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Additional Info Card */}
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
            mb: 3,
            border: '1px solid #f0f0f0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ 
                mb: 2, 
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#1a1a1a'
              }}>
                Product Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                    Brand
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                    {product.brand || 'NISTYLEZ'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                    Category
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                    {product.category || 'General'}
                  </Typography>
                </Box>
                                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                   <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                     Stock Status
                   </Typography>
                   <Typography variant="body2" sx={{ 
                     fontWeight: 500, 
                     fontSize: '0.85rem',
                     color: product.inStock ? '#2e7d32' : '#d32f2f'
                   }}>
                     {product.inStock ? 'In Stock' : 'Out of Stock'}
                   </Typography>
                 </Box>
                 {/* Add padding after stock status */}
                 <Box sx={{ height: 8 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Bottom Action Bar */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 56,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ px: 2, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  fontSize: '1.3rem',
                  color: '#1976d2'
                }}>
                  {formatCurrency(product.customerSalePrice || product.customerPrice || product.price || 0)}
                </Typography>
                {product.customerPrice && product.customerSalePrice && product.customerPrice > product.customerSalePrice && (
                  <Chip
                    label={`${product.customerDiscountPercentage || calculateDiscountPercentage(product.customerPrice, product.customerSalePrice)}% OFF`}
                    sx={{
                      backgroundColor: '#ff6b35',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22
                    }}
                  />
                )}
              </Box>
              {product.customerPrice && product.customerSalePrice && product.customerPrice > product.customerSalePrice && (
                <Typography variant="body2" sx={{ 
                  textDecoration: 'line-through', 
                  color: '#999', 
                  fontSize: '0.85rem'
                }}>
                  {formatCurrency(product.customerPrice || 0)}
                </Typography>
              )}
            </Box>
            
            {!cartItemExists ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<ShoppingCart />}
                onClick={handleAddToCart}
                disabled={!product.inStock}
                sx={{
                  bgcolor: '#1976d2',
                  px: 3,
                  py: 1,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  minWidth: 'auto',
                  '&:hover': { bgcolor: '#1565c0' }
                }}
              >
                Add to Cart
              </Button>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={() => handleQuantityChange(quantity - 1)}
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    width: 32, 
                    height: 32,
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <Remove sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography variant="h6" sx={{ 
                  minWidth: 30, 
                  textAlign: 'center', 
                  fontSize: '1rem',
                  fontWeight: 600
                }}>
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => handleQuantityChange(quantity + 1)}
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    width: 32, 
                    height: 32,
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
                <Chip 
                  label={`In Cart (${cartQuantity})`} 
                  color="success" 
                  size="medium"
                  sx={{ fontSize: '0.8rem', height: 32 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <BottomNavigation />

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {product.name} added to cart!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductDetails; 